import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { adminLogin, admin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // If already logged in, redirect to dashboard
  if (admin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password) {
      toast.error('Please enter both username and password');
      return;
    }

    setLoading(true);
    try {
      await adminLogin(username, password);
      toast.success('Admin login successful');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#003366] flex flex-col items-center justify-center py-12 px-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/">
            <img 
              src={LOGO_URL} 
              alt="CodeXero" 
              className="h-12 mx-auto mb-6"
              onError={(e) => { e.target.style.display = 'none' }}
            />
          </Link>
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-sm mb-4">
            <Shield className="h-4 w-4 text-[#008080]" />
            <span className="text-white text-sm font-medium">Admin Portal</span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-8 shadow-[0_4px_24px_rgba(0,51,102,0.2)]">
          <h1 className="text-2xl font-[800] text-[#003366] mb-2 text-center" data-testid="admin-login-heading">
            Admin Login
          </h1>
          <p className="text-slate-600 text-center mb-8">
            Access the CX0 DPDP Admin Dashboard
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="username" className="text-[#003366] font-medium">Username</Label>
              <Input
                id="username"
                data-testid="admin-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1 rounded-sm"
                placeholder="admin@codexero.ai"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-[#003366] font-medium">Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  data-testid="admin-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-sm pr-10"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="admin-login-btn"
              className="w-full bg-[#003366] hover:bg-[#002244] text-white py-6 rounded-sm btn-hover"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login to Admin Dashboard'
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-white/60 text-sm mt-6">
          Not an admin?{' '}
          <Link to="/login" className="text-[#008080] hover:underline">
            Go to User Login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AdminLoginPage;
